import React from "react";

import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import { isFeatureFlagEnabled } from "@/lib/feature-flags";
import { useDynamicFeatureFlags } from "@/services/dynamic-feature-flags/components/DynamicFeatureFlagsProvider";
import { DynamicFeatureFlagsThemeVariants } from "@/services/dynamic-feature-flags/models";

import { useSecondaryLayoutTopBannerContext } from "../context";
import { BlackFridayBanner } from "./BlackFridayBanner";
import { ChristmasBanner } from "./ChristmasBanner";
import { ElevenYearBanners } from "./ElevenYearsBanner";
import { HalloweenBanner } from "./HalloweenBanner";
import { SpringBanner } from "./SpringBanner";
import { ValentinesDayBanner } from "./ValentinesDayBanner";

const bannersPool = [
  {
    isActive: isFeatureFlagEnabled("BLACK_FRIDAY_THEME"),
    Component: BlackFridayBanner,
  },
  {
    isActive: isFeatureFlagEnabled("HALLOWEEN_THEME"),
    Component: HalloweenBanner,
  },
  {
    isActive: isFeatureFlagEnabled("CHRISTMAS_THEME"),
    Component: ChristmasBanner,
  },
  {
    isActive: isFeatureFlagEnabled("ELEVEN_YEARS_THEME"),
    Component: ElevenYearBanners,
  },
  {
    isActive: isFeatureFlagEnabled("VALENTINES_DAY_THEME"),
    Component: ValentinesDayBanner,
  },
];

type DynamicFlagThemePool<T extends React.FC = React.FC> = Record<
  DynamicFeatureFlagsThemeVariants,
  T
>;

const dynamicFlagThemePool: DynamicFlagThemePool = {
  THEME_SPRING_OFFER: SpringBanner,
};

export const SecondaryLayoutTopBannerRenderer: React.FC = () => {
  const { isBannerHidden } = useSecondaryLayoutTopBannerContext();
  // TODO: maybe there should be a union literal type later for "themes"
  const { themes } = useDynamicFeatureFlags();

  const activeDynamicBannerKey = Object.entries(themes).find(
    ([_key, value]) => value === true,
  )?.[0] as keyof typeof themes | undefined;

  const TargetDynamicBannerComponent = activeDynamicBannerKey
    ? dynamicFlagThemePool[activeDynamicBannerKey]
    : null;

  const targetBanner = bannersPool.find(({ isActive }) => isActive);
  const BannerComponent =
    targetBanner?.Component || TargetDynamicBannerComponent || (() => null);

  return (
    <ConditionalRender on={!isBannerHidden}>
      <BannerComponent />
    </ConditionalRender>
  );
};
